import { useContext, useEffect, useState, Fragment } from "react";

import ListPage from "../../../shared/components/list-page/list-page";
import { IColumn } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import { roleWrite, roleAdmin, roleRead } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { getAllData, deleteMultiple, IListPageData } from "../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";

import "./report-list.scss";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";

import { getTheParamBeforeID } from "../../../shared/utils/utils";

export const resultsColumns: IColumn[] = [
  {
    key: "name",
    displayName: "Name",
    dataType: {
      type: "text",
    },
  },
  {
    key: "date",
    displayName: "Date",
    dataType: {
      type: "text",
    },
  },
  {
    key: "state",
    displayName: "State",
    dataType: {
      type: "text",
    },
  },
];

const ReportList = () => {
  const [reportsData, setReportsData] = useState<IListPageData | undefined>({rows: []});
  const [unblockInput, setUnblockInput] = useState(false);
  const [sortedBy, setSortedBy] = useState("?order_by=date desc");
  const [currentOffset, setCurrentOffset] = useState(1);
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const { sendRequest: getDataset, status: datasetStatus, data: datasetLoadedData } = useHttp(getAllData, false, authCtx.token, "dataset", "analytics");
  const { sendRequest: getModelConfig, status: modelConfigStatus, data: modelConfigLoadedData, error: modelConfigError } = useHttp(getAllData, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getSortBy, status: getSortByStatus, data: getSortByData, error: errorGetSortByData } = useHttp(getAllData, false, authCtx.token, undefined, "analytics");
  const { sendRequest: deleteMultipleRequest,status: allStatusDelete,data: deleteData,error: errorDelete } = useHttp(deleteMultiple, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getSortByPagination, status: getSortByPaginationStatus, data: getSortByPaginationData, error: getSortByPaginationError } = useHttp(getAllData, false, authCtx.token, undefined, "analytics");

  const params = useParams();
  const findParent = getTheParamBeforeID(params.id);

  useEffect(() => {
    if (findParent && findParent === "datasets") {
      getSortBy(`dataset/${params.id}/reports`, `${sortedBy}&limit=50&offset=0`);
      getDataset(`dataset/${params.id}`);
    }
    if (findParent && findParent === "modelconfig") {
      getSortBy(`modelconfig/${params.id}/reports`, `${sortedBy}&limit=50&offset=0`);
      getModelConfig(`modelconfig/${params.id}`);
    }
  }, []);

  useEffect(() => {
    if (modelConfigStatus === "completed" && !modelConfigError) {
      getDataset(`dataset/${modelConfigLoadedData?.rows.dataset_id}`);
    }
  }, [modelConfigStatus]);

  const onTableScrollAction = (offset: number) => {
    if (reportsData && reportsData.rows.length >= 50) {
      setCurrentOffset(offset);
      const defineParentString = findParent === "datasets"? "dataset" : "modelconfig";

      if (!getSortByPaginationData) {
        getSortByPagination(`${defineParentString}/${params.id}/reports`, `${sortedBy}&limit=50&offset=${offset * 50}`);
      } else if (getSortByPaginationData.rows.length !== 0) {
        getSortByPagination(`${defineParentString}/${params.id}/reports`, `${sortedBy}&limit=50&offset=${offset * 50}`);
      }
    }
  };

  useEffect(() => {
    if (getSortByPaginationStatus === "completed" && !getSortByPaginationError) {
      reportsData && setReportsData({rows: [...reportsData.rows, ...getSortByPaginationData.rows]});
    }
  }, [getSortByPaginationStatus]);

  useEffect(() => {
    if (getSortByStatus === "completed" && !errorGetSortByData) {
      setReportsData(getSortByData);
    }
  }, [getSortByStatus]);

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleAdmin) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    if (allStatusDelete === "completed" && !errorDelete) {
      const filteredDataByDate = {
        rows: deleteData.rows.sort(function (a: any, b: any) { // eslint-disable-line
          return new Date(a.date).valueOf() - new Date(b.date).valueOf();
        })
      };
      setReportsData(filteredDataByDate);
    }
  }, [allStatusDelete]);

  const handleOnDelete = (ids: string[]) => {
    if (findParent && findParent === "datasets") {
      deleteMultipleRequest(`dataset/${params.id}/reports${sortedBy}&limit=${currentOffset * 50}&offset=0`, { ids });
    }
    if (findParent && findParent === "modelconfig") {
      deleteMultipleRequest(`modelconfig/${params.id}/reports${sortedBy}&limit=${currentOffset * 50}&offset=0`, { ids });
    }
  };

  const onRowClickAction = (id: string) => {
    const rolesArray = authCtx.selectedTenant.roles;
    if ((rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleRead) !== -1 )) {
      navigate(`/analytics/reports/${id}`);
    }
  };

  const sortBy = (sortingQuery: string) => {
    const findParent = getTheParamBeforeID(params.id);

    if (findParent && findParent === "datasets") {
      getSortBy(`dataset/${params.id}/reports`, `${sortingQuery}&limit=${currentOffset * 50}&offset=0`);
    }
    if (findParent && findParent === "modelconfig") {
      getSortBy(`modelconfig/${params.id}/reports`, `${sortingQuery}&limit=${currentOffset * 50}&offset=0`);
    }
    setSortedBy(sortingQuery);
  };

  const renderPathButtons = () => {
    let findId = "";
    let currentMsg = "";

    if (findParent && findParent === "datasets" && params.id) {
      findId = params.id;
      currentMsg = datasetLoadedData ? (datasetLoadedData.dataset_name + " dataset") : "Dataset";
    }
    else if (findParent && findParent === "modelconfig") {
      findId = modelConfigLoadedData ? modelConfigLoadedData.rows.id : "";
      currentMsg = modelConfigLoadedData?.rows?.modelversion_name ? (modelConfigLoadedData.rows.modelversion_name + " model") : "Modelconfig";
    }

    return (
      <span>
        <span onClick={() => navigate("/analytics/datasets")} className="optionPath">Datasets</span>
        <span className="optionPathDelimiter">-</span>
        {findParent === "modelconfig" && datasetLoadedData && 
        <Fragment>
          <span onClick={() => navigate(`/analytics/datasets-details/${datasetLoadedData.id}`)} className="optionPath">{datasetLoadedData.dataset_name + " dataset"}</span>
          <span className="optionPathDelimiter">-</span>
        </Fragment>
        }
        <span onClick={() => navigate(`/analytics/${findParent}/${findId}`)} className="optionPath">{currentMsg}</span>
        <span className="optionPathDelimiter">-</span>
        <span className="selectedPathBtn">Reports</span>
      </span>
    );
  };

  return (
    <div className="reportList">
      {validateLoadingStatusNew([allStatusDelete, datasetStatus, modelConfigStatus, getSortByStatus, getSortByPaginationStatus]) && <Spinner />}
      {
        <ListPage
          title={"Reports"}
          sortBy={sortBy}
          defaultSortCol={"date"}
          onTableScrollAction={onTableScrollAction}
          tooltipTxt={`Reports overview, of the selected ${getTheParamBeforeID(params.id) === "datasets" ? "dataset" : "model"}`}
          columns={resultsColumns}
          unblockInput={unblockInput}
          dataUrl={"/analytics/datasets/:id/reports"}
          pageMode={"nonEditable"}
          handleOnDelete={handleOnDelete}
          fetchedData={reportsData}
          allStatus={"completed"}
          hideButtons={["add"]}
          onRowClickAction={onRowClickAction}
          renderPathButtons={renderPathButtons}
        />
      }
    </div>
  );
};

export default ReportList;
